import { differenceInMilliseconds } from "date-fns/differenceInMilliseconds";
import { format } from "date-fns/format";
import { TransportTimetableDeparture } from "../components/TransportTimetable/TransportTimetable";
import { DeparturesByDestination } from "../components/DeparturesListCard/DeparturesListCard";
import { TransportInformation } from "../hooks/useTransportService";
import groupBy from "lodash.groupby";
import { RailAirDeparture } from "../components/RailAirDeparturesListCard/RailAirDeparturesListCard";

export const formatDepartureTime = (
  departureTime: string,
  now: Date,
  t: (key: string) => string,
) => {
  const diffMillis = differenceInMilliseconds(new Date(departureTime), now);

  const minutesDifference = Math.round(diffMillis / (1000 * 60)); // convert milliseconds to minutes

  // Set to "Due" if less than 1 minute. Else check other conditions
  return minutesDifference <= 1
    ? t("poi.departures.due")
    : minutesDifference <= 60
      ? `${minutesDifference} ${t("poi.departures.minutes")}`
      : format(new Date(departureTime), "HH:mm");
};

export const formatBusDepartures = (
  transportInformation: TransportInformation | undefined,
  now: Date,
  t: (key: string) => string,
): TransportTimetableDeparture[] => {
  if (!transportInformation) return [];
  return transportInformation.services
    .reduce((acc: TransportTimetableDeparture[], service) => {
      service.departures.forEach((departure) => {
        acc.push({
          destination: departure.destination,
          departureTime: formatDepartureTime(
            departure.expectedDeparturetime,
            now,
            t,
          ),
          transportIdentifier: {
            code: service.lineName,
            colour: service.lineColour,
          },
          departureTimeISO: departure.expectedDeparturetime,
        });
      });

      return acc;
    }, [])
    .sort((a, b) => {
      return (
        new Date(a.departureTimeISO).getTime() -
        new Date(b.departureTimeISO).getTime()
      );
    });
};

interface MetroServiceDepartures {
  code: string;
  colour: string;
  departures: DeparturesByDestination;
}

export const formatMetroDepartures = (
  transportInformation: TransportInformation | undefined,
): MetroServiceDepartures[] => {
  if (!transportInformation) return [];

  return transportInformation.services.map(
    (service): MetroServiceDepartures => {
      const departuresByDestionation: DeparturesByDestination = {};

      // Group departures by destination
      const destinationDepartures = Object.values(
        groupBy(service.departures, (departure) => departure.destination),
      );

      // For each destination's departures...
      destinationDepartures.forEach((departures) => {
        const [{ destination }] = departures;

        const formattedDepartures = departures
          .filter(({ expectedDeparturetime }, index) => {
            // Filter out departures that have an expected departure time
            // within 10 seconds of any previously iterated departures.
            // This is done by comparing the current departure time with
            // the times of all previous departures, and if a previous
            // departure time is found that's within 10 seconds of the
            // current one, the current departure is excluded.
            const departureTime = new Date(expectedDeparturetime).getTime();
            const previousDepartures = departures.slice(0, index);

            return previousDepartures.every((d) => {
              const previousDepartureTime = new Date(
                d.expectedDeparturetime,
              ).getTime();
              const timeDifference = Math.abs(
                departureTime - previousDepartureTime,
              );
              return timeDifference > 10000; // 10 seconds
            });
          })
          .map(({ expectedDeparturetime: departureTimeISO }) => {
            return { destination, departureTimeISO };
          })
          .sort((departureA, departureB) => {
            return (
              new Date(departureA.departureTimeISO).getTime() -
              new Date(departureB.departureTimeISO).getTime()
            );
          });

        departuresByDestionation[destination] = {
          departures: formattedDepartures,
        };
      });

      return {
        code: service.lineName,
        colour: service.lineColour,
        departures: departuresByDestionation,
      };
    },
  );
};

export const formatRailDepartures = (
  transportInformation: TransportInformation | undefined,
): RailAirDeparture[] => {
  if (!transportInformation) return [];
  return transportInformation.services
    .reduce((acc: RailAirDeparture[], service) => {
      service.departures.forEach((departure) => {
        acc.push({
          destination: departure.destination,
          expectedDepartureTime: departure.expectedDeparturetime,
          scheduledDepartureTime: departure.scheduledDepartureTime,
          service: service.lineName,
          platform: departure.platform,
        });
      });

      return acc;
    }, [])
    .sort((departureA, departureB) => {
      return (
        new Date(departureA.expectedDepartureTime).getTime() -
        new Date(departureB.expectedDepartureTime).getTime()
      );
    });
};
